import {
  QI_IMPROVEMENTS_V1_FLAG,
  loadQiImprovementsEnabled,
} from '@/lib/qi-ledger/improvements-flag';
import {
  CARE_TODAY_V1_FLAG,
  loadCareTodayEnabled,
} from '@/lib/client-care-redesign/care-today-flag';
import { UNIFIED_INCIDENT_CAPTURE_FLAG } from '@/lib/incidents/unified-capture';
import { assertSettingsSessionControl } from '@/lib/settings/settings-session-access';
import { isTenantScopedRosterChangesFlag } from '@/lib/roster/changes-flag';
import { isTenantScopedRosterEvidenceFlag } from '@/lib/roster/scoped-gate-evidence';
import {
  isTenantScopedRosterAppEventsFlag,
  loadRosterAppEventsEnabled,
} from '@/lib/roster/app-events-flag';
import { isTenantScopedMasterGridFlag } from '@/lib/roster/master-grid-flag';
import { isTenantScopedRosterPublishUnblockFlag } from '@/lib/roster/publish-unblock-flag';
import { isTenantScopedRosterImportV2Flag } from '@/lib/roster/roster-import-v2-flag';
import { isTenantScopedRosterGuidedPublishFlag } from '@/lib/roster/guided-publish-flag';
import { isTenantScopedSahScheduleGridFlag } from '@/lib/roster/sah-schedule-grid-flag';
import { isTenantScopedRosterApplyClarityFlag } from '@/lib/roster/apply-clarity';
import { isTenantScopedRosterSettingsFlag } from '@/lib/roster/settings-hub-flag';
import { createClient } from './client';
import {
  isContainedDispatchReliabilityEnablement,
  isTenantScopedDispatchReliabilityFlagDbKey,
} from '@/lib/roster/dispatch-reliability-flags';
import { isTenantScopedClientCareRedesignFlag } from '@/lib/client-care-redesign/feature-flags';
import { isTenantScopedStructuredCarePlannerFlag } from '@/lib/care-plans/structured-care-planner-feature-flag';
import {
  isMedeaRosterHorizonEnablement,
  isRosterHorizonFlagDbKey,
} from '@/lib/roster/roster-horizon-flags';
import {
  loadTenantRolloutState,
  type TenantRolloutStateSnapshot,
} from '@/lib/roster/tenant-rollout-state';
import { isRetiredSahFlagKey, isSahUtopiaFlagKey } from '@/lib/sah/sah-utopia-feature-flags';
import { MIXED_WORKSPACE_FEATURE_FLAG } from '@/lib/navigation/provider-type';

type RolloutStateClient = Parameters<typeof loadTenantRolloutState>[0];

function rolloutStateClient(): RolloutStateClient {
  return supabase() as unknown as RolloutStateClient;
}

function isTenantOnlyDispatchFlagDbKey(flagKey: string | null | undefined): boolean {
  return isTenantScopedDispatchReliabilityFlagDbKey(flagKey) || isRosterHorizonFlagDbKey(flagKey);
}

async function isContainedTenantOnlyDispatchEnablement(
  flagKey: string | null | undefined,
  tenantId: string | null | undefined,
  requireReadableState = false,
): Promise<boolean> {
  if (isMedeaRosterHorizonEnablement(flagKey, tenantId)) return true;
  const { state } = await loadTenantRolloutState(rolloutStateClient(), tenantId, {
    strict: requireReadableState,
  });
  return isContainedDispatchReliabilityEnablement(flagKey, state);
}

function isTenantOnlyFlag(flagKey: string): boolean {
  return (
    flagKey === 'family_app_r1' ||
    flagKey === MIXED_WORKSPACE_FEATURE_FLAG ||
    flagKey === QI_IMPROVEMENTS_V1_FLAG ||
    flagKey === CARE_TODAY_V1_FLAG ||
    flagKey === 'pay_setup_v2' ||
    flagKey === 'incident_medical_episode_additional_types_v1' ||
    flagKey === UNIFIED_INCIDENT_CAPTURE_FLAG ||
    isTenantOnlyDispatchFlagDbKey(flagKey) ||
    isTenantScopedClientCareRedesignFlag(flagKey) ||
    isTenantScopedStructuredCarePlannerFlag(flagKey) ||
    isTenantScopedRosterEvidenceFlag(flagKey) ||
    isTenantScopedRosterImportV2Flag(flagKey) ||
    isTenantScopedSahScheduleGridFlag(flagKey) ||
    isTenantScopedRosterSettingsFlag(flagKey) ||
    isTenantScopedRosterChangesFlag(flagKey) ||
    isTenantScopedMasterGridFlag(flagKey) ||
    isTenantScopedRosterGuidedPublishFlag(flagKey) ||
    isTenantScopedRosterAppEventsFlag(flagKey) ||
    isTenantScopedRosterPublishUnblockFlag(flagKey) ||
    isTenantScopedRosterApplyClarityFlag(flagKey) ||
    isSahUtopiaFlagKey(flagKey)
  );
}

export {
  SAH_UTOPIA_FLAG_DESCRIPTIONS,
  SAH_UTOPIA_FLAG_KEYS,
  isSahUtopiaFlagEnabled,
  isSahUtopiaFlagKey,
  isRetiredSahFlagKey,
  type SahUtopiaFlagKey,
} from '@/lib/sah/sah-utopia-feature-flags';

const supabase = () => createClient();

/**
 * Check if a feature flag is enabled. Production-sensitive roster dispatch
 * controls require an explicit tenant row; other flags retain tenant-to-global
 * fallback.
 */
export async function isFeatureEnabled(flagKey: string, tenantId?: string): Promise<boolean> {
  const pending = resolveFeatureEnabled(flagKey, tenantId);
  return isTenantScopedRosterPublishUnblockFlag(flagKey) ? pending.catch(() => false) : pending;
}
async function resolveFeatureEnabled(flagKey: string, tenantId?: string): Promise<boolean> {
  const sb = supabase();
  if (flagKey === QI_IMPROVEMENTS_V1_FLAG) return loadQiImprovementsEnabled(sb, tenantId);
  if (flagKey === CARE_TODAY_V1_FLAG) return loadCareTodayEnabled(sb, tenantId);
  if (isTenantScopedRosterAppEventsFlag(flagKey)) return loadRosterAppEventsEnabled(sb, tenantId);

  if (isTenantOnlyFlag(flagKey) && !tenantId) return false;
  if (await isContainedTenantOnlyDispatchEnablement(flagKey, tenantId)) return false;

  const query = sb
    .from('feature_flags')
    .select('enabled')
    .eq('flag_key', flagKey)
    .order('tenant_id', { ascending: false, nullsFirst: false })
    .limit(1);

  if (tenantId && isTenantOnlyFlag(flagKey)) {
    query.eq('tenant_id', tenantId);
  } else if (tenantId) {
    query.or(`tenant_id.eq.${tenantId},tenant_id.is.null`);
  } else {
    query.is('tenant_id', null);
  }

  const { data, error } = await query;
  if (
    error &&
    (isTenantScopedRosterEvidenceFlag(flagKey) ||
      isTenantScopedRosterSettingsFlag(flagKey) ||
      isTenantScopedRosterImportV2Flag(flagKey) ||
      isTenantScopedRosterApplyClarityFlag(flagKey) ||
      isTenantScopedSahScheduleGridFlag(flagKey) ||
      isTenantScopedRosterChangesFlag(flagKey) ||
      isTenantScopedMasterGridFlag(flagKey) ||
      isTenantScopedRosterGuidedPublishFlag(flagKey))
  )
    return false;
  if (error) throw error;
  return data?.[0]?.enabled ?? false;
}

/**
 * Set a feature flag value.
 * If tenantId is omitted, sets the global flag.
 */
export async function setFeatureFlag(
  flagKey: string,
  enabled: boolean,
  tenantId?: string,
): Promise<void> {
  const sb = supabase();

  await assertSettingsSessionControl(sb, '/settings/feature-flags', 'setFeatureFlag', tenantId);
  const normalizedFlagKey = flagKey.trim();
  if (
    !tenantId &&
    (normalizedFlagKey === 'family_app_r1' || normalizedFlagKey === MIXED_WORKSPACE_FEATURE_FLAG)
  ) {
    throw new Error('feature_rollout_requires_tenant_scope');
  }
  if (
    !tenantId &&
    (normalizedFlagKey === QI_IMPROVEMENTS_V1_FLAG ||
      normalizedFlagKey === CARE_TODAY_V1_FLAG ||
      normalizedFlagKey === 'pay_setup_v2' ||
      isTenantScopedRosterEvidenceFlag(normalizedFlagKey) ||
      isTenantScopedRosterImportV2Flag(normalizedFlagKey) ||
      isTenantScopedSahScheduleGridFlag(normalizedFlagKey) ||
      isTenantScopedRosterSettingsFlag(normalizedFlagKey) ||
      isTenantScopedRosterChangesFlag(normalizedFlagKey) ||
      isTenantScopedMasterGridFlag(normalizedFlagKey) ||
      isTenantScopedRosterGuidedPublishFlag(normalizedFlagKey) ||
      isTenantScopedRosterAppEventsFlag(normalizedFlagKey) ||
      isTenantScopedRosterPublishUnblockFlag(normalizedFlagKey) ||
      isTenantScopedRosterApplyClarityFlag(normalizedFlagKey))
  ) {
    throw new Error('feature_rollout_requires_tenant_scope');
  }
  if (isSahUtopiaFlagKey(normalizedFlagKey) || isRetiredSahFlagKey(normalizedFlagKey)) {
    throw new Error('provider_operational_control_preview_required');
  }
  if (!tenantId && isTenantScopedDispatchReliabilityFlagDbKey(normalizedFlagKey)) {
    throw new Error('roster_dispatch_rollout_requires_tenant_scope');
  }
  if (enabled && !tenantId && isRosterHorizonFlagDbKey(normalizedFlagKey)) {
    throw new Error('roster_dispatch_rollout_requires_tenant_scope');
  }
  if (!tenantId && isTenantScopedClientCareRedesignFlag(normalizedFlagKey)) {
    throw new Error('feature_rollout_requires_tenant_scope');
  }
  if (!tenantId && isTenantScopedStructuredCarePlannerFlag(normalizedFlagKey)) {
    throw new Error('feature_rollout_requires_tenant_scope');
  }
  if (
    enabled &&
    isTenantScopedDispatchReliabilityFlagDbKey(normalizedFlagKey) &&
    (await isContainedTenantOnlyDispatchEnablement(normalizedFlagKey, tenantId, true))
  ) {
    throw new Error('roster_dispatch_rollout_contained');
  }
  const { error } = await sb.rpc('set_feature_flag_controlled', {
    p_payload: {
      tenant_id: tenantId || null,
      flag_key: normalizedFlagKey,
      enabled,
    },
  });
  if (error) throw error;
}

/**
 * Get all feature flags for a tenant (merged with globals). Tenant-specific
 * values override global values, while roster dispatch controls ignore global
 * rows and remain false unless the tenant is explicitly opted in.
 */
export async function getFeatureFlags(tenantId?: string): Promise<Record<string, boolean>> {
  const sb = supabase();

  const query = sb
    .from('feature_flags')
    .select('flag_key, enabled, tenant_id')
    .order('tenant_id', { ascending: false, nullsFirst: false });

  if (tenantId) {
    query.or(`tenant_id.eq.${tenantId},tenant_id.is.null`);
  } else {
    query.is('tenant_id', null);
  }

  const { data, error } = await query;
  if (error) throw error;

  const { state: rollout } = await loadTenantRolloutState(rolloutStateClient(), tenantId);

  // Build map: tenant-specific wins over global
  // Since we ordered tenant_id DESC (nulls last), tenant-specific comes first.
  // Process in reverse so tenant-specific overwrites global.
  const flags: Record<string, boolean> = {};
  for (const row of (data || []).reverse()) {
    if (
      isMedeaRosterHorizonEnablement(row.flag_key, tenantId) ||
      isContainedDispatchReliabilityEnablement(row.flag_key, rollout)
    ) {
      flags[row.flag_key] = false;
      continue;
    }
    if (isTenantOnlyFlag(row.flag_key) && row.tenant_id !== tenantId) {
      flags[row.flag_key] = false;
      continue;
    }
    flags[row.flag_key] = row.enabled;
  }
  return flags;
}

export async function getTenantRolloutStateForSettings(
  tenantId: string,
): Promise<TenantRolloutStateSnapshot> {
  // Operational consumers retain the brand_config fallback so a mixed-version
  // deployment cannot interrupt a provider. The editor is strict: a failed
  // compatibility read must never be rendered as saveable defaults.
  return loadTenantRolloutState(rolloutStateClient(), tenantId, { strict: true });
}

/**
 * Get all feature flag rows for a tenant (for the admin settings page).
 * Returns full objects including description and tenant_id.
 */
export async function getFeatureFlagRows(tenantId?: string): Promise<
  Array<{
    id: string;
    tenant_id: string | null;
    flag_key: string;
    enabled: boolean;
    description: string | null;
    updated_at: string;
  }>
> {
  const sb = supabase();

  const query = sb
    .from('feature_flags')
    .select('id, tenant_id, flag_key, enabled, description, updated_at')
    .order('flag_key', { ascending: true });

  if (tenantId) {
    query.or(`tenant_id.eq.${tenantId},tenant_id.is.null`);
  } else {
    query.is('tenant_id', null);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data || []).filter(
    (row) => !isSahUtopiaFlagKey(row.flag_key) && !isRetiredSahFlagKey(row.flag_key),
  );
}
